import React, { useState, useMemo } from 'react';
import { FilterContent, SearchWrapper } from './filter-management-users.styles';
import { FilterManagementUsersProps } from './types';
import { Search } from '@shared/ui/search/search';
import { Filter } from '@shared/ui/filter/filter';
import { DEFAULT_USERS_LIST_PARAMETERS } from '@entities/user/config/store-constants';
import { OrganizationsListDataRequest } from '@entities/organization/api/organization-types';
import { useOrganizationsInfinityListQuery } from '@entities/organization/api/organization-api';
import { Select } from '@shared/ui/select/select';
import { useOrganizationSelect } from '@features/catalog/mutate-catalog/lib/hooks/use-organization-options/use-organization-options';
import { USER_ROLE_OPTIONS, USER_STATUS_OPTIONS } from '@entities/user/config/ui-constants';
import { UserRoles } from '@shared/model/user';
import { UserProfileOptionDataRequest } from '@entities/user-profile-option/api/user-profile-option-types';
import { DEFAULT_USER_PROFILE_OPTIONS_LIST_PARAMETERS } from '@entities/user-profile-option/config/store-constants';
import { useUserProfileOptionsInfinityListQuery } from '@entities/user-profile-option/api/user-profile-option-api';
import { useDepartmentSelect } from '@entities/user-profile-option/libs/hooks/use-department-options/use-department-options';
import { SORT_USERS_CLEAR_PARAMETERS } from './constants';

export const FilterManagementUsers: React.FC<FilterManagementUsersProps> = ({
    usersListParameters,
    setUsersListParameters,
}) => {
    /* Organizations */
    const [organizationsListParameters, setOrganizationsListParameters] = useState<OrganizationsListDataRequest>({
        take: 10,
    });
    const {
        data: organizationsList,
        isLoading: isOrganizationsLoading,
        isFetching: isOrganizationsFetching,
    } = useOrganizationsInfinityListQuery(organizationsListParameters);
    const {
        organizationOptions,
        organizationEmptyOption,
        isShowEmptyLoaderOrganizations,
        infinityScrollOrganizationsProps,
        onSearchOrganizations,
    } = useOrganizationSelect({
        organizationsData: organizationsList,
        isLoading: isOrganizationsLoading,
        isFetching: isOrganizationsFetching,
        setOrganizationsListParameters,
    });

    /* Department */
    const [departmentListParameters, setDepartmentListParameters] = useState<UserProfileOptionDataRequest>({
        ...DEFAULT_USER_PROFILE_OPTIONS_LIST_PARAMETERS,
        take: 10,
    });
    const {
        data: departmentsList,
        isLoading: isDepartmentsListLoading,
        isFetching: isDepartmentsListFetching,
    } = useUserProfileOptionsInfinityListQuery(departmentListParameters);

    const {
        departmentOptions,
        departmentEmptyOption,
        isShowEmptyLoaderDepartment,
        infinityScrollDepartmentProps,
        onSearchDepartment,
    } = useDepartmentSelect({
        departmentsData: departmentsList,
        isLoading: isDepartmentsListLoading,
        isFetching: isDepartmentsListFetching,
        setDepartmentListParameters,
    });

    const selectedOrganizations = useMemo(() => {
        return organizationOptions.filter((organization) =>
            usersListParameters.companies?.includes(organization.value)
        );
    }, [setUsersListParameters, usersListParameters.companies]);

    const selectedDepartments = useMemo(() => {
        return departmentOptions.filter((option) => usersListParameters.departments?.includes(Number(option.value)));
    }, [setUsersListParameters, usersListParameters.departments]);

    return (
        <SearchWrapper>
            <Search
                placeholder='Поиск по ФИО, Организации, ИНН'
                sortParametersAfterClearSearch={SORT_USERS_CLEAR_PARAMETERS}
                parameters={usersListParameters}
                setParameters={setUsersListParameters}
            />
            <Filter onResetAllFilters={() => setUsersListParameters(DEFAULT_USERS_LIST_PARAMETERS)}>
                <FilterContent>
                    <Select
                        multiselect
                        isAsync
                        clearable
                        label='Организации'
                        placeholder='Выберите организации из списка'
                        isShowPreloaderInList={isShowEmptyLoaderOrganizations}
                        onSearch={onSearchOrganizations}
                        infinityScrollProps={infinityScrollOrganizationsProps}
                        options={organizationOptions}
                        values={selectedOrganizations || organizationEmptyOption}
                        onChange={(values) =>
                            setUsersListParameters((prev) => ({
                                ...prev,
                                companies: values.map((company) => company.value),
                            }))
                        }
                    />
                    <Select
                        label='Статус'
                        placeholder='Выберите из списка'
                        values={USER_STATUS_OPTIONS.filter(
                            (option) => option.value === String(usersListParameters.active)
                        )}
                        options={USER_STATUS_OPTIONS}
                        onChange={(value) =>
                            setUsersListParameters((prev) => ({
                                ...prev,
                                active: value[0]?.value ? value[0].value === 'true' : null,
                            }))
                        }
                    />
                    <Select
                        label='Роль'
                        placeholder='Выберите из списка'
                        values={USER_ROLE_OPTIONS.filter((option) => option.value === usersListParameters.role)}
                        options={USER_ROLE_OPTIONS}
                        onChange={(value) =>
                            setUsersListParameters((prev) => ({
                                ...prev,
                                role: value[0]?.value as UserRoles,
                            }))
                        }
                    />
                    <Select
                        isAsync
                        clearable
                        multiselect
                        label='Выберите департамент'
                        placeholder='Выберите департамент из списка'
                        isShowPreloaderInList={isShowEmptyLoaderDepartment}
                        infinityScrollProps={infinityScrollDepartmentProps}
                        onSearch={onSearchDepartment}
                        values={selectedDepartments || departmentEmptyOption}
                        options={departmentOptions}
                        onChange={(value) =>
                            setUsersListParameters((prev) => ({
                                ...prev,
                                departments: value.map((option) => Number(option.value)),
                            }))
                        }
                    />
                </FilterContent>
            </Filter>
        </SearchWrapper>
    );
};
